/**
 *
 * @module environmentConfiguration
 *
 */

import Device from './../device';

/**
 *
 * @class EnvironmentConfiguration
 * @desc Provides configuration information that describes the current environment
 * and is used by the ConfigurationClient to retrieve the SdkSessionConfiguration
 * necessary to create an SdkSession instance.
 *
 */
export default class EnvironmentConfiguration {

    /**
     *
     * @param {String} applicationRuntime
     * @param {String} applicationRuntimeProfile
     * @param {String} applicationVersion
     * @param {String} deviceFamily
     * @param {String} deviceProfile
     * @param {String} deviceRuntimeProfile
     * @param {String} sdkVersion
     * @param {String} configVersion
     *
     */
    constructor({ applicationRuntime, applicationRuntimeProfile, applicationVersion,
        deviceFamily, deviceProfile, deviceRuntimeProfile,
        sdkVersion, configVersion } = {}) {

        /**
         *
         * @access public
         * @type {String}
         * @desc Gets or sets the application runtime.
         *
         */
        this.applicationRuntime = applicationRuntime || '';

        /**
         *
         * @access public
         * @type {String}
         * @desc Gets or sets the application runtime profile (e.g. "debug", "release").
         *
         */
        this.applicationRuntimeProfile = applicationRuntimeProfile || 'release';

        /**
         *
         * @access public
         * @type {String}
         * @desc Gets or sets the application version.
         *
         */
        this.applicationVersion = applicationVersion || '';

        /**
         *
         * @access public
         * @type {String}
         * @desc Gets or sets the device family (e.g. "browser").
         *
         */
        this.deviceFamily = deviceFamily || 'browser';

        /**
         *
         * @access public
         * @type {String}
         * @desc Gets or sets the device profile (e.g. "desktop").
         *
         */
        this.deviceProfile = deviceProfile || '';

        /**
         *
         * @access public
         * @type {String}
         * @desc Gets or sets the device runtime profile.
         *
         */
        this.deviceRuntimeProfile = deviceRuntimeProfile || '';

        /**
         *
         * @access public
         * @type {String}
         * @desc Gets or sets the SDK version (e.g. "3.9").
         *
         */
        this.sdkVersion = sdkVersion || '3.9';

        /**
         *
         * @access public
         * @type {String}
         * @desc Gets or sets the configuration version (e.g. "2.0").
         *
         */
        this.configVersion = configVersion || '2.0';

        /**
         *
         * @access public
         * @type {String}
         *
         */
        this.platform = '';

        /**
         *
         * @access private
         * @type {Device}
         *
         */
        this.device = new Device();

        /**
         *
         * @desc Initialize properties based on runtime device values.
         *
         */
        this.init();

    }

    /* #region private: */

    /**
     *
     * @access private
     * @desc Initialize the environment config properties based on runtime device values.
     *
     */
    init() {

        const { device, deviceProfile, deviceRuntimeProfile, applicationRuntime } = this;

        if (!deviceProfile || deviceProfile.trim() === '') {

            if (device.is.desktop || device.is.tablet || device.is.phone) {
                this.deviceProfile = device.details.name;
            } else if (device.is.smarttv || device.is.connecteddevice) {
                this.deviceProfile = device.details.family;
            }

        }

        if (!deviceRuntimeProfile || deviceRuntimeProfile.trim() === '') {
            this.deviceRuntimeProfile = device.platformVersion;
        }

        if (!applicationRuntime || applicationRuntime.trim() === '') {

            if (device.is.desktop || device.is.tablet || device.is.phone) {
                this.applicationRuntime = device.browser;
            } else if (device.is.smarttv || device.is.connecteddevice) {
                this.applicationRuntime = device.details.name;
            }

        }

        this.platform = device.platform;

    }

}



// WEBPACK FOOTER //
// ./src/services/platforms/isomorphic/environmentConfiguration.js